import { type IColor } from 'react-color-palette';

import { rgbToHsv } from '@air/utils-color';

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const toHex = (value: number) => value.toString(16).padStart(2, '0');

/**
 * Sanitizes color values from react-color-palette to ensure RGB values are valid integers
 * between 0-255. The library can sometimes output decimal values or values outside the
 * valid range (including negative values) when dragging near the edges of the color picker,
 * resulting in invalid hex codes like "#ff-2-2".
 *
 * This function ensures all three color representations (RGB, HSV, hex) are consistent
 * by using the sanitized RGB values as the source of truth and recalculating HSV and hex.
 */
export const sanitizeColorValues = (color: IColor): IColor => {
  // Clamp and round RGB values to valid integers (0-255)
  const r = Math.round(clamp(color.rgb.r, 0, 255));
  const g = Math.round(clamp(color.rgb.g, 0, 255));
  const b = Math.round(clamp(color.rgb.b, 0, 255));
  // Recalculate HSV from sanitized RGB to ensure consistency
  const hsv = rgbToHsv(r, g, b);
  // Regenerate hex from sanitized RGB values
  const sanitizedHex = `#${toHex(r)}${toHex(g)}${toHex(b)}`;

  // When the color is fully desaturated (s = 0), preserve the original hue value
  // since the hue doesn't affect the visual appearance but users may want to adjust it
  const preservedHue = hsv.s === 0 ? color.hsv.h : hsv.h;

  return {
    ...color,
    hex: sanitizedHex,
    hsv: {
      a: color.hsv.a,
      h: preservedHue,
      s: hsv.s,
      v: hsv.v,
    },
    rgb: {
      a: color.rgb.a,
      b,
      g,
      r,
    },
  };
};
